import type { AgentActivityEvent } from "@wakeloop/schemas/types";
import { buildControllerTaskLifecycleEvent } from "./controller-task-lifecycle-hook";
import { enqueueControllerTaskLifecycleEventWhileLocked } from "./controller-task-lifecycle-store";
import { enqueueAgentActivityHookEvent } from "./hook-inbox";
import {
  buildAgentActivityEvent,
  type ObservedAgentSession,
  resolveObservedAgentActivityBinding,
} from "./observer-adapters";
import {
  listProjectBindings,
  runIfAgentActivityBindingGenerationIsCurrent,
} from "./project-bindings";
import {
  normalizeSharedSessionTitle,
  readCodexSessionTitles,
} from "./provider-session-titles";

export class AgentControllerTaskLifecycleDurabilityError extends Error {
  constructor(cause: unknown) {
    super("Agent Controller task lifecycle evidence could not be persisted.", {
      cause,
    });
    this.name = "AgentControllerTaskLifecycleDurabilityError";
  }
}

export async function ingestAgentActivityHook(input: {
  homeDir?: string;
  nowMs?: number;
  payload: unknown;
  provider: "claude-code" | "codex" | "opencode";
  rootDir?: string;
}): Promise<"ignored" | "queued" | "reconciled" | "reused" | "unbound"> {
  if (!isRecord(input.payload)) {
    return "ignored";
  }
  const cwd = text(input.payload.cwd);
  const providerSessionId =
    text(input.payload.session_id) ?? text(input.payload.sessionId);
  if (!(cwd && providerSessionId)) {
    return "ignored";
  }
  const nowMs = input.nowMs ?? Date.now();
  const hookEvent = text(input.payload.hook_event_name) ?? "unknown";
  const status = hookStatus(hookEvent, input.payload.status);
  const bindings = await listProjectBindings({
    rootDir: input.rootDir,
  });
  const binding = await resolveObservedAgentActivityBinding(cwd, bindings);
  if (!binding) {
    return "unbound";
  }
  const lifecycleEvent =
    input.provider === "opencode"
      ? null
      : buildControllerTaskLifecycleEvent({
          nowMs,
          organizationId: binding.organizationId,
          payload: input.payload,
          provider: input.provider,
        });
  const lifecycleOutcome = lifecycleEvent
    ? await runIfAgentActivityBindingGenerationIsCurrent(
        {
          bindingGeneration: binding.bindingGeneration,
          organizationId: binding.organizationId,
          projectId: binding.projectId,
          rootDir: input.rootDir,
        },
        async () => {
          try {
            return await enqueueControllerTaskLifecycleEventWhileLocked({
              event: lifecycleEvent,
              nowMs,
              rootDir: input.rootDir,
            });
          } catch (error) {
            throw new AgentControllerTaskLifecycleDurabilityError(error);
          }
        }
      )
    : null;
  const title =
    normalizeSharedSessionTitle(
      input.payload.session_title ?? input.payload.sessionTitle
    ) ??
    (input.provider === "codex"
      ? ((await readCodexSessionTitles({ homeDir: input.homeDir })).get(
          providerSessionId
        ) ?? null)
      : null);
  const observed: ObservedAgentSession = {
    clientKind: resolveHookClientKind(input.provider, input.payload),
    cwd,
    eventType: hookEventType(hookEvent, status),
    gitBranch: nullableText(input.payload.gitBranch),
    gitCommit: null,
    model: nullableText(input.payload.model),
    observedAtMs: nowMs,
    provider: input.provider,
    providerMetadata: resolveHookProviderMetadata(
      input.provider,
      input.payload
    ),
    providerSessionId,
    startedAtMs: hookEvent === "SessionStart" ? nowMs : null,
    status,
    title,
  };
  const event = await buildAgentActivityEvent({
    bindings: [binding],
    observed,
  });
  if (!event) {
    return lifecycleOutcome ?? "ignored";
  }
  const activityOutcome = await runIfAgentActivityBindingGenerationIsCurrent(
    {
      bindingGeneration: binding.bindingGeneration,
      organizationId: binding.organizationId,
      projectId: binding.projectId,
      rootDir: input.rootDir,
    },
    async () =>
      await enqueueAgentActivityHookEvent({
        bindingGeneration: binding.bindingGeneration,
        event,
        rootDir: input.rootDir,
      })
  );
  if (lifecycleOutcome === "queued" || activityOutcome === "queued") {
    return "queued";
  }
  if (lifecycleOutcome === "reused" || activityOutcome === "reused") {
    return "reused";
  }
  return "unbound";
}

function hookEventType(
  name: string,
  status: AgentActivityEvent["status"]
): AgentActivityEvent["eventType"] {
  if (name === "SessionStart" || name === "session.created") {
    return "session_started";
  }
  if (name === "UserPromptSubmit" || status === "active") {
    return "session_active";
  }
  if (status === "idle") {
    return "session_idle";
  }
  if (status === "ended") {
    return "session_ended";
  }
  if (status === "error") {
    return "session_error";
  }
  return "session_updated";
}

function resolveHookClientKind(
  provider: "claude-code" | "codex" | "opencode",
  payload: Record<string, unknown>
) {
  if (provider === "claude-code") {
    return "claude-code-cli" as const;
  }
  return provider === "codex"
    ? normalizeCodexClient(payload.originator)
    : ("unknown" as const);
}

function resolveHookProviderMetadata(
  provider: "claude-code" | "codex" | "opencode",
  payload: Record<string, unknown>
) {
  if (provider === "claude-code") {
    return compact({
      permissionMode: enumValue(
        payload.permission_mode ?? payload.permissionMode,
        [
          "default",
          "acceptEdits",
          "plan",
          "dontAsk",
          "bypassPermissions",
          "ask",
          "allow",
        ]
      ),
      source: "hook",
    });
  }
  if (provider === "codex") {
    return compact({
      approvalPolicy: enumValue(payload.approval_policy, [
        "untrusted",
        "on-failure",
        "on-request",
        "never",
      ]),
      originator: normalizeCodexOriginator(payload.originator),
      source: "hook",
    });
  }
  return {};
}

export async function readAgentActivityHookStdin(input?: {
  stream?: NodeJS.ReadableStream;
}) {
  const stream = input?.stream ?? process.stdin;
  let raw = "";
  for await (const chunk of stream) {
    raw += String(chunk);
    if (Buffer.byteLength(raw) > 64 * 1024) {
      return null;
    }
  }
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return null;
  }
}

function hookStatus(
  name: string,
  providerStatus: unknown
): AgentActivityEvent["status"] {
  if (name === "session.error") {
    return "error";
  }
  if (name === "session.deleted") {
    return "ended";
  }
  if (name === "session.status") {
    const normalized = text(providerStatus);
    if (normalized === "idle") {
      return "idle";
    }
    if (normalized === "busy" || normalized === "retry") {
      return "active";
    }
    return "unknown";
  }
  if (name === "session.created" || name === "session.updated") {
    return "unknown";
  }
  if (name === "Stop" || name === "Notification" || name === "session.idle") {
    return "idle";
  }
  return "active";
}

function normalizeCodexClient(value: unknown) {
  return text(value) === "Codex Desktop" ? "codex-app" : "unknown";
}

function normalizeCodexOriginator(value: unknown) {
  if (text(value) === "Codex Desktop") {
    return "desktop" as const;
  }
  if (text(value)) {
    return "cli" as const;
  }
  return undefined;
}

function enumValue<const T extends readonly string[]>(
  value: unknown,
  allowed: T
) {
  const normalized = text(value);
  return normalized && allowed.includes(normalized) ? normalized : undefined;
}

function compact(value: Record<string, unknown>) {
  return Object.fromEntries(
    Object.entries(value).filter(([, entry]) => entry !== undefined)
  );
}

function nullableText(value: unknown) {
  const normalized = text(value);
  return normalized && normalized.length <= 512 ? normalized : null;
}

function text(value: unknown) {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}
